import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import {
  Form,
  Card,
  CardContent,
  Button,
  toast,
  Typography,
  LoadingButton,
  Stack,
} from "@altimateai/lego";
import { useForm, useWatch } from "react-hook-form";
import { getIconByDatastoreType } from "../../../DatastoreImage";
import { ConnectionProps } from "../types";
import { DatastoreTypeEnum } from "@/pages/datastores/types";
import { DatabricksAuthType, DatabricksAuthTypeValue } from "@/pages/datastores/databricksTypes";
import DatabricksFormFields from "./DatabricksFormFields";
import { useDatabricksConnection } from "./useDatabricksConnection";
import { AUTH_INFO_CONTENT, DatabricksFormData, formSchema } from "./schema";

export type { DatabricksFormData };

interface DatabricksConnectionProps extends ConnectionProps {
  /**
   * Delay (ms) before the cold-start hint appears under the Test Connection
   * button. Defaults to 10s in production; tests override to a smaller value.
   */
  coldStartHintAfterMs?: number;
}

const DatabricksConnection = ({
  typeData,
  handleBack,
  coldStartHintAfterMs = 10_000,
}: DatabricksConnectionProps): JSX.Element => {
  const form = useForm<DatabricksFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: typeData.name || "",
      description: typeData.description || "",
      server_hostname: (typeData.connection?.server_hostname as string) || "",
      http_path: (typeData.connection?.http_path as string) || "",
      auth_type: (typeData.connection?.auth_type as DatabricksAuthTypeValue) || null,
      access_token: (typeData.connection?.access_token as string) || "",
      client_id: (typeData.connection?.client_id as string) || "",
      client_secret: (typeData.connection?.client_secret as string) || "",
      azure_tenant_id: (typeData.connection?.azure_tenant_id as string) || "",
    },
    // `onChange` keeps the button-enabled state live as users type. We
    // explored `onBlur` (PR #2155 review F8) to skip per-keystroke
    // validation, but the test suite (and real users) expect the Test
    // Connection button to enable mid-typing once required fields are
    // filled. Net cost is small in practice — the form has ≤10 fields.
    mode: "onChange",
  });

  const authType = useWatch({ control: form.control, name: "auth_type" });
  // Snapshot of the form values at the last Test Connection click.
  // Used to invalidate isTestSuccess only when the user edits a field
  // AFTER the test ran — previously this was tracked via form.formState.isDirty
  // with a `form.reset(form.getValues())` hack, which had race conditions
  // around stacked clicks (PR #2155 review F2).
  const lastTestedValues = useRef<DatabricksFormData | null>(null);
  const watchedValues = useWatch({ control: form.control });

  const {
    testConnection,
    isTestingConnection,
    isTestSuccess,
    testResult,
    resetTestResult,
    createConnection,
    isCreating,
  } = useDatabricksConnection();

  // Surface a cold-start hint after the test has been pending for ~10s.
  // Databricks SQL warehouses auto-pause after idle and cold-start takes
  // 10s (serverless) to a few minutes (classic). Without this hint the
  // user can't tell whether the BE is hung or the warehouse is just slow
  // to wake. Resets to false when the test finishes (success or error).
  const [showColdStartHint, setShowColdStartHint] = useState(false);
  useEffect(() => {
    if (!isTestingConnection) {
      setShowColdStartHint(false);
      return;
    }
    const timer = setTimeout(() => setShowColdStartHint(true), coldStartHintAfterMs);
    return () => clearTimeout(timer);
  }, [isTestingConnection, coldStartHintAfterMs]);

  // Invalidate the test result when the form values diverge from the
  // snapshot we tested with. Stable shallow comparison; only the fields
  // that affect the test outcome are checked.
  useEffect(() => {
    if (!isTestSuccess || !lastTestedValues.current) return;
    const tested = lastTestedValues.current;
    const current = form.getValues();
    const fieldsToCompare: (keyof DatabricksFormData)[] = [
      "server_hostname",
      "http_path",
      "auth_type",
      "access_token",
      "client_id",
      "client_secret",
      "azure_tenant_id",
    ];
    if (fieldsToCompare.some(f => tested[f] !== current[f])) {
      resetTestResult();
      lastTestedValues.current = null;
    }
    // `watchedValues` keeps this effect in sync with form changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues, isTestSuccess, resetTestResult]);

  // Also reset when auth type changes (the secret fields swap entirely)
  useEffect(() => {
    resetTestResult();
    lastTestedValues.current = null;
  }, [authType, resetTestResult]);

  const handleTestConnection = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const currentAuthType = form.getValues("auth_type");
    const baseFields = ["server_hostname", "http_path"] as const;
    let authFields: readonly (keyof DatabricksFormData)[];
    if (currentAuthType === DatabricksAuthType.AZURE_SP) {
      authFields = ["azure_tenant_id", "client_id", "client_secret"] as const;
    } else if (currentAuthType === DatabricksAuthType.OAUTH_M2M) {
      authFields = ["client_id", "client_secret"] as const;
    } else {
      authFields = ["access_token"] as const;
    }
    const fieldsToValidate = [...baseFields, ...authFields];
    const isValid = await form.trigger(fieldsToValidate);
    if (isValid) {
      lastTestedValues.current = form.getValues();
      testConnection(form.getValues());
    }
  };

  const onSubmit = (data: DatabricksFormData) => {
    if (!isTestSuccess) {
      toast({ variant: "destructive", title: "Please test connection first" });
      return;
    }
    createConnection(data);
  };

  const icon = getIconByDatastoreType({
    datastoreType: DatastoreTypeEnum.DATABRICKS,
    className: "al-w-10 al-h-10 al-mr-3",
  });

  return (
    <Card>
      <CardContent>
        <Stack direction="row" align="center" className="al-mb-6 al-pt-6">
          {icon}
          <div>
            <Typography variant="subheading">Databricks Connection</Typography>
            <Typography size="sm" className="al-text-muted-foreground">
              Connect your Databricks SQL Warehouse
            </Typography>
          </div>
        </Stack>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="al-space-y-4">
            <div className="al-p-4 al-bg-blue-50 al-border al-border-blue-200 al-rounded-md al-mb-4">
              <Typography size="sm" className="al-text-blue-800">
                {AUTH_INFO_CONTENT[authType ?? ""] || AUTH_INFO_CONTENT[""]}
              </Typography>
            </div>

            <DatabricksFormFields control={form.control} />

            {isTestSuccess && testResult && (
              <div
                className="al-p-4 al-bg-green-50 al-border al-border-green-200 al-rounded-md"
                role="status"
                data-testid="connection-validated-banner"
              >
                <Stack direction="row" align="center" gap="sm">
                  <IconCircleCheckFilled size={16} className="al-text-green-700" aria-hidden />
                  <Typography size="sm" className="al-text-green-800">
                    Connection validated
                    {testResult.catalogs?.length
                      ? `. Found ${testResult.catalogs.length} catalog(s): ${testResult.catalogs.slice(0, 3).join(", ")}${testResult.catalogs.length > 3 ? "..." : ""}`
                      : ""}
                  </Typography>
                </Stack>
              </div>
            )}

            {isTestingConnection && showColdStartHint && (
              <div
                className="al-p-3 al-bg-blue-50 al-border al-border-blue-200 al-rounded-md"
                role="status"
                aria-live="polite"
                data-testid="cold-start-hint"
              >
                <Typography size="sm" className="al-text-blue-800">
                  Still validating — your SQL warehouse may be cold-starting. Paused warehouses can
                  take a few minutes to come online.
                </Typography>
              </div>
            )}

            <Stack direction="row" justify="between" className="al-pt-4">
              <Button variant="outline" asChild>
                <Link to="/settings/datastores">Cancel</Link>
              </Button>
              <Stack gap="md">
                <Button variant="secondary" onClick={handleBack}>
                  Back
                </Button>
                <LoadingButton
                  variant="default"
                  disabled={!form.formState.isValid || isTestingConnection}
                  onClick={handleTestConnection}
                  loading={isTestingConnection}
                >
                  {isTestingConnection ? "Testing…" : "Test Connection"}
                </LoadingButton>
                <LoadingButton
                  variant="default"
                  type="submit"
                  loading={isCreating}
                  disabled={!form.formState.isValid || !isTestSuccess || isTestingConnection}
                >
                  Save Connection
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default DatabricksConnection;
